import { usePostQCSlideMutation } from 'store/apis/slidesApi';
import { checkIfFailedQC } from 'components/Modals/QCPassFailModal/utilities';
import moment from 'moment/moment';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { useState } from 'react';
import {
  ML_QC_OPTIONS_RECORDS,
  QC_FAILED_STATE_CONSTANT,
  QC_PASSED_STATE_CONSTANT,
} from 'components/QCViewer/constants';

export const useQCPassFailSubmit = (
  slide,
  qcChecks,
  handleQCReviewed,
  selectedRows,
  setModalOpen,
  getNextRow,
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [internal_notes, setInternalNotes] = useState('');
  const [external_comment, setExternalComment] = useState('');
  const [postQCSlide] = usePostQCSlideMutation();
  const { showError } = useSnackbar();

  const getQCPostStates = () =>
    ML_QC_OPTIONS_RECORDS.reduce((prevValue, currValue) => {
      if (!currValue.stateAttribute) {
        return prevValue;
      }

      return {
        ...prevValue,
        [currValue.stateAttribute]: qcChecks[currValue.attribute]
          ? QC_FAILED_STATE_CONSTANT
          : QC_PASSED_STATE_CONSTANT,
      };
    }, {});

  const getPrimaryFailReasonAndRedoAction = () => {
    const checkedOptions = ML_QC_OPTIONS_RECORDS.filter(
      ({ attribute }) => qcChecks[attribute],
    );
    const mainOption = checkedOptions.sort(
      (a, b) => b.reasonPriority - a.reasonPriority,
    )[0];

    return [mainOption.failConditionReasonName, mainOption.redoAction];
  };

  const getFailedQCPostParams = () => {
    const [primaryFailReason, redoAction] = getPrimaryFailReasonAndRedoAction();
    const qcPostStates = getQCPostStates();

    return {
      ...qcPostStates,
      failed_qc_reason: primaryFailReason,
      needs_redo: true,
      needs_redo_type: redoAction,
      failed_qc_datetime: moment().toISOString(),
    };
  };

  const getPostParams = () => {
    const passedQC = !checkIfFailedQC(qcChecks);

    return {
      internal_notes,
      ...(external_comment && { external_comment }),
      ...(passedQC && { passed_qc_datetime: moment().toISOString() }),
      ...(!passedQC && getFailedQCPostParams()),
    };
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    const postParams = getPostParams();

    postQCSlide({ slideUUID: slide.uuid, postParams })
      .unwrap()
      .then(() => {
        handleQCReviewed({ slide });

        if (selectedRows && selectedRows.length === 1) {
          // turn off modals if this is the last one
          setModalOpen((prev) => !prev);
          // if only one is selected, then move onto the next one
          getNextRow();
        }
      })
      .catch(() => showError('Failed to submit QC review. Please try again.'))
      .finally(() => setIsSubmitting(false));
  };

  return {
    setInternalNotes,
    setExternalComment,
    isSubmitting,
    handleSubmit,
  };
};
